import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import DOMPurify from 'dompurify';
import { Component } from 'react';
import { PresentationEditorWithControls } from './editor/PresentationEditorWithControls';
// Error Boundary for Presentation Components
class PresentationErrorBoundary extends Component {
    static getDerivedStateFromError(error) {
        return {
            hasError: true,
            error
        };
    }
    componentDidCatch(error, errorInfo) {
        console.error('Presentation component error:', error, errorInfo);
    }
    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }
            return /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col items-center justify-center p-8 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "text-red-600 dark:text-red-400 text-lg font-semibold mb-2",
                        children: "Presentation Error"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "text-red-500 dark:text-red-300 text-sm mb-4",
                        children: this.state.error?.message || 'Failed to render presentation'
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: ()=>this.setState({
                                hasError: false,
                                error: undefined
                            }),
                        className: "px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors",
                        children: "Try Again"
                    })
                ]
            });
        }
        return this.props.children;
    }
    constructor(props){
        super(props);
        this.state = {
            hasError: false
        };
    }
}
/**
 * Check if HTML is a rich presentation with inline styles
 */ function isRichPresentation(html) {
    if (!html) return false;
    const hasInlineStyles = /<style[\s\S]*?>[\s\S]*?<\/style>/i.test(html);
    const hasDataTheme = /data-theme=/i.test(html);
    return hasInlineStyles || hasDataTheme;
}
/**
 * Parse HTML content to extract individual slides with style info
 */ function parseSlides(htmlContent) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const sections = doc.querySelectorAll('section');
    const styleEl = doc.querySelector('style');
    const styleTag = styleEl ? styleEl.outerHTML : '';
    // Extract data-theme from wrapper div
    const themeWrapper = doc.querySelector('[data-theme]');
    const dataTheme = themeWrapper?.getAttribute('data-theme') || 'federal-navy';
    return {
        slides: Array.from(sections).map((section)=>section.outerHTML),
        styleTag,
        dataTheme
    };
}
const PresentationPreview = ({ content })=>{
    const { slides, styleTag, dataTheme } = parseSlides(content);
    const isRich = isRichPresentation(content);
    return /*#__PURE__*/ _jsx("div", {
        className: "overflow-x-auto p-2",
        children: slides.length === 0 ? /*#__PURE__*/ _jsx("div", {
            className: "text-center text-gray-500 dark:text-gray-400 py-8",
            children: "No slides yet..."
        }) : /*#__PURE__*/ _jsx("div", {
            className: "flex gap-3 pb-2",
            children: slides.map((slideContent, index)=>/*#__PURE__*/ _jsx("div", {
                    className: "flex-shrink-0 w-72 h-44 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden",
                    children: isRich ? // Rich presentation: render with styles, scaled down
                    /*#__PURE__*/ _jsxs("div", {
                        "data-theme": dataTheme,
                        className: "reveal w-full h-full origin-top-left",
                        style: {
                            transform: 'scale(0.25)',
                            width: '400%',
                            height: '400%'
                        },
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                dangerouslySetInnerHTML: {
                                    __html: styleTag
                                }
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "slides",
                                // biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized with DOMPurify
                                dangerouslySetInnerHTML: {
                                    __html: DOMPurify.sanitize(slideContent, {
                                        ADD_TAGS: [
                                            'section'
                                        ],
                                        ADD_ATTR: [
                                            'class'
                                        ]
                                    })
                                }
                            })
                        ]
                    }) : // Simple presentation: use prose styling
                    /*#__PURE__*/ _jsx("div", {
                        className: "p-2 prose prose-sm dark:prose-invert max-w-none overflow-hidden bg-white dark:bg-gray-800",
                        style: {
                            fontSize: '10px'
                        },
                        // biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized via DOMParser
                        dangerouslySetInnerHTML: {
                            __html: DOMPurify.sanitize(slideContent)
                        }
                    })
                }, index))
        })
    });
};
const PresentationEditor = ({ value, onChange })=>{
    return /*#__PURE__*/ _jsx(PresentationEditorWithControls, {
        value: value,
        onChange: onChange
    });
};
// Wrapped components with error boundaries
const PresentationPreviewWithErrorBoundary = (props)=>/*#__PURE__*/ _jsx(PresentationErrorBoundary, {
        children: /*#__PURE__*/ _jsx(PresentationPreview, {
            ...props
        })
    });
const PresentationEditorWithErrorBoundary = (props)=>/*#__PURE__*/ _jsx(PresentationErrorBoundary, {
        children: /*#__PURE__*/ _jsx(PresentationEditor, {
            ...props
        })
    });
export const presentationArtifact = {
    kind: 'presentation',
    description: 'Interactive presentation with reveal.js - preview shows horizontal slide list, editor shows full interactive presentation',
    Preview: PresentationPreviewWithErrorBoundary,
    Editor: PresentationEditorWithErrorBoundary
};
